import React from 'react';
import { Grid, User, ChevronLeft, ChevronRight } from 'lucide-react';

// View modes for session display
export type ViewMode = 'table' | 'left' | 'right' | 'seat';

interface LayoutSelectorProps {
    viewMode: ViewMode;
    focusedSeat: number | null;
    playerCount: number;
    currentTurnSeat?: number;
    onViewModeChange: (mode: ViewMode, seatIndex?: number) => void;
}

/**
 * LayoutSelector - Controls for switching between view modes
 * 
 * Modes:
 * - Table: See all players in grid (2x2, 2x3, 2x4 depending on count)
 * - Left/Right: Focus on adjacent seats
 * - Seat #: Direct focus on specific seat (1-8)
 * 
 * Hotkeys: T (Table), ← (Left), → (Right), 1-8 (Seat)
 */
export function LayoutSelector({
    viewMode,
    focusedSeat,
    playerCount,
    currentTurnSeat = 0,
    onViewModeChange,
}: LayoutSelectorProps) {

    // Calculate which seat to show for left/right navigation
    const getAdjacentSeat = (direction: 'left' | 'right'): number => {
        const current = focusedSeat ?? currentTurnSeat;
        if (direction === 'left') {
            return (current - 1 + playerCount) % playerCount;
        }
        return (current + 1) % playerCount;
    };

    const buttonClass = (isActive: boolean) => `
        px-2 py-1.5 rounded text-xs font-medium transition-all
        ${isActive
            ? 'bg-purple-500/40 text-purple-200 border border-purple-400/50'
            : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
        }
    `;

    return (
        <div className="flex items-center gap-1 bg-black/30 rounded-lg p-1 border border-white/10">
            {/* Table View */}
            <button
                onClick={() => onViewModeChange('table')}
                className={buttonClass(viewMode === 'table')}
                title="Table view (T)"
            >
                <Grid className="size-4" />
            </button>


            {/* Divider */}
            <div className="w-px h-5 bg-white/10 mx-1" />

            {/* Left Navigation */}
            <button
                onClick={() => onViewModeChange('seat', getAdjacentSeat('left'))}
                className={buttonClass(viewMode === 'left')}
                title="Previous seat (←)"
            >
                <ChevronLeft className="size-4" />
            </button>

            {/* Seat Direct Select */}
            <div className="flex items-center gap-0.5">
                {Array.from({ length: Math.min(playerCount, 8) }).map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => onViewModeChange('seat', idx)}
                        className={`
                            w-5 h-5 rounded text-[10px] font-bold transition-all
                            ${viewMode === 'seat' && focusedSeat === idx
                                ? 'bg-cyan-500/40 text-cyan-200 border border-cyan-400/50'
                                : idx === currentTurnSeat
                                    ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30'
                                    : 'bg-white/5 text-white/40 hover:bg-white/10 hover:text-white/60'
                            }
                        `}
                        title={`Seat ${idx + 1} (${idx + 1})`}
                    >
                        {idx + 1}
                    </button>
                ))}
            </div>

            {/* Right Navigation */}
            <button
                onClick={() => onViewModeChange('seat', getAdjacentSeat('right'))}
                className={buttonClass(viewMode === 'right')}
                title="Next seat (→)"
            >
                <ChevronRight className="size-4" />
            </button>
        </div>
    );
}

/**
 * Hook for keyboard shortcuts
 */
export function useLayoutHotkeys(
    onViewModeChange: (mode: ViewMode, seatIndex?: number) => void,
    playerCount: number
) {
    React.useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't capture when typing in inputs
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
                return;
            }

            const key = e.key.toLowerCase();

            switch (key) {
                case 't':
                    onViewModeChange('table');
                    break;

                case 'arrowleft':
                    onViewModeChange('left');
                    break;
                case 'arrowright':
                    onViewModeChange('right');
                    break;
                default:
                    // Check for number keys 1-8
                    const num = parseInt(key);
                    if (num >= 1 && num <= Math.min(playerCount, 8)) {
                        onViewModeChange('seat', num - 1);
                    }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onViewModeChange, playerCount]);
}

/**
 * Get grid layout classes based on player count and view mode
 */
export function getGridLayoutClass(playerCount: number, viewMode: ViewMode): string {
    if (viewMode === 'seat') {
        // Single focused player
        return 'grid-cols-1';
    }


    // Table view - grid based on player count
    if (playerCount <= 2) return 'grid-cols-2';
    if (playerCount <= 4) return 'grid-cols-2';
    if (playerCount <= 6) return 'grid-cols-3';
    return 'grid-cols-4';
}

/**
 * Get grid row classes based on player count
 */
export function getGridRowClass(playerCount: number, viewMode: ViewMode): string {
    if (viewMode !== 'table') return '';

    if (playerCount <= 2) return 'grid-rows-1';
    if (playerCount <= 4) return 'grid-rows-2';
    if (playerCount <= 6) return 'grid-rows-2';
    return 'grid-rows-2';
}


// ============================================================================
// FORMATION DEFINITIONS — CSS Grid layouts for player grids
// ============================================================================

export interface Formation {
    id: string;
    label: string;
    playerCount: number;
    gridTemplateAreas: string;
    gridTemplateColumns: string;
    gridTemplateRows: string;
}

export const FORMATIONS: Record<number, Formation[]> = {
    2: [
        { id: '2A', label: 'Side by Side', playerCount: 2, gridTemplateAreas: '"s0 s1"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr' },
        { id: '2B', label: 'Stacked', playerCount: 2, gridTemplateAreas: '"s0" "s1"', gridTemplateColumns: '1fr', gridTemplateRows: '1fr 1fr' },
    ],
    3: [
        { id: '3A', label: '1 Wide + 2', playerCount: 3, gridTemplateAreas: '"s0 s0" "s1 s2"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' },
        { id: '3B', label: '2 + 1 Wide', playerCount: 3, gridTemplateAreas: '"s0 s1" "s2 s2"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' },
    ],
    4: [
        { id: '4A', label: '2×2 Grid', playerCount: 4, gridTemplateAreas: '"s0 s1" "s2 s3"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' },
        { id: '4B', label: 'Sandwich', playerCount: 4, gridTemplateAreas: '"s0 s0" "s1 s2" "s3 s3"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr 1fr' },
    ],
    5: [
        { id: '5A', label: '3 + 2', playerCount: 5, gridTemplateAreas: '"s0 s0 s1 s1 s2 s2" ". s3 s3 s4 s4 ."', gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: '1fr 1fr' },
        { id: '5B', label: '2 + 3', playerCount: 5, gridTemplateAreas: '". s0 s0 s1 s1 ." "s2 s2 s3 s3 s4 s4"', gridTemplateColumns: 'repeat(6, 1fr)', gridTemplateRows: '1fr 1fr' },
    ],
    6: [
        { id: '6A', label: '3×2 Wide', playerCount: 6, gridTemplateAreas: '"s0 s1 s2" "s3 s4 s5"', gridTemplateColumns: '1fr 1fr 1fr', gridTemplateRows: '1fr 1fr' },
        { id: '6B', label: '2×3 Tall', playerCount: 6, gridTemplateAreas: '"s0 s1" "s2 s3" "s4 s5"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr 1fr' },
    ],
    7: [
        { id: '7A', label: '4 + 3', playerCount: 7, gridTemplateAreas: '"s0 s0 s0 s1 s1 s1 s2 s2 s2 s3 s3 s3" "s4 s4 s4 s4 s5 s5 s5 s5 s6 s6 s6 s6"', gridTemplateColumns: 'repeat(12, 1fr)', gridTemplateRows: '1fr 1fr' },
        { id: '7B', label: '3 + 4', playerCount: 7, gridTemplateAreas: '"s0 s0 s0 s0 s1 s1 s1 s1 s2 s2 s2 s2" "s3 s3 s3 s4 s4 s4 s5 s5 s5 s6 s6 s6"', gridTemplateColumns: 'repeat(12, 1fr)', gridTemplateRows: '1fr 1fr' },
    ],
    8: [
        { id: '8A', label: '4×2 Wide', playerCount: 8, gridTemplateAreas: '"s0 s1 s2 s3" "s4 s5 s6 s7"', gridTemplateColumns: 'repeat(4, 1fr)', gridTemplateRows: '1fr 1fr' },
        { id: '8B', label: '2×4 Tall', playerCount: 8, gridTemplateAreas: '"s0 s1" "s2 s3" "s4 s5" "s6 s7"', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr 1fr 1fr' },
    ],
};

export function getDefaultFormation(playerCount: number): string {
    return FORMATIONS[playerCount]?.[0]?.id || '4A';
}

export function getFormationById(formationId: string): Formation | undefined {
    for (const formations of Object.values(FORMATIONS)) {
        const found = formations.find(f => f.id === formationId);
        if (found) return found;
    }
    return undefined;
}

// Formation Picker — visual CSS grid thumbnails like SpellTable mobile
export function FormationPicker({
    playerCount,
    selectedFormation,
    onSelect,
}: {
    playerCount: number;
    selectedFormation: string;
    onSelect: (formationId: string) => void;
}) {
    const formations = FORMATIONS[playerCount] || [];
    if (formations.length <= 1) return null;

    return (
        <div className="flex gap-3 justify-center">
            {formations.map(f => {
                const sel = selectedFormation === f.id;
                return (
                    <button
                        key={f.id}
                        onClick={() => onSelect(f.id)}
                        className={`relative p-2 rounded-lg border-2 transition-all ${sel
                            ? 'border-purple-500 bg-purple-500/20 shadow-lg shadow-purple-500/20'
                            : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
                            }`}
                    >
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateAreas: f.gridTemplateAreas,
                                gridTemplateColumns: f.gridTemplateColumns,
                                gridTemplateRows: f.gridTemplateRows,
                                width: '72px',
                                height: '48px',
                                gap: '2px',
                            }}
                        >
                            {Array.from({ length: f.playerCount }, (_, i) => (
                                <div
                                    key={i}
                                    style={{ gridArea: `s${i}` }}
                                    className={`rounded-sm flex items-center justify-center text-[7px] font-bold ${sel
                                        ? 'bg-purple-400/60 text-purple-100'
                                        : 'bg-white/20 text-white/40'
                                        }`}
                                >
                                    {i + 1}
                                </div>
                            ))}
                        </div>
                        <p className={`text-[9px] mt-1 text-center ${sel ? 'text-purple-300' : 'text-white/40'}`}>
                            {f.label}
                        </p>
                    </button>
                );
            })}
        </div>
    );
}

export default LayoutSelector;
